import { useEffect, useRef } from 'react';

// Detects extremely rapid mouse movement that could indicate
// someone grabbing the device or a panic scenario on desktop.
// Threshold is in pixels per 50ms interval — normal fast mouse movement
// rarely exceeds 500px/50ms, while a snatch/grab can exceed 1000+.
//
// Detection is *sustained*: the callback fires only after REQUIRED_STREAK
// consecutive over-threshold samples (a continuous burst of fast motion).
// A single quick flick — or any gap longer than the 50ms window — resets the
// streak, so ordinary fast mouse use no longer triggers the auto-logout.
export const REQUIRED_STREAK = 3;
// Below this gap (ms) two mousemove samples are effectively the same frame; measuring
// speed across them is unreliable (a near-zero dt inflates it), so they are skipped.
export const MIN_DT_MS = 8;
// px per 50ms. Pending real-device calibration: the previous 1500 (30 000 px/s) was
// likely unreachable on touchpads.
export const PANIC_SPEED_PX_PER_50MS = 1000;

export const useMousePanic = (
  callback,
  { enabled = true, threshold = PANIC_SPEED_PX_PER_50MS } = {}
) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;
    // Only run on non-touch devices (desktop)
    if ('ontouchstart' in window) return;

    let lastX = null;
    let lastY = null;
    let lastTime = 0;
    let panicStreak = 0;
    let triggered = false;

    const handler = (event) => {
      if (triggered) return;

      const now = Date.now();
      const elapsed = now - lastTime;

      // Wait for a measurable interval. Sub-frame gaps (e.g. high-polling mice firing
      // every ~1ms) would divide distance by a near-zero dt and fabricate a panic speed.
      // Keep the existing reference point — don't advance it — so the next event measures
      // over the accumulated distance/time once enough has elapsed.
      if (lastX !== null && elapsed < MIN_DT_MS) return;

      if (lastX !== null && elapsed < 50) {
        const dx = event.clientX - lastX;
        const dy = event.clientY - lastY;
        const distance = Math.sqrt(dx ** 2 + dy ** 2);
        // Normalize to distance per 50ms for consistent threshold
        const speed = (distance / elapsed) * 50;

        if (speed >= threshold) {
          panicStreak += 1;
          if (panicStreak >= REQUIRED_STREAK) {
            triggered = true;
            window.removeEventListener('mousemove', handler);
            callbackRef.current(speed);
          }
        } else {
          panicStreak = 0;
        }
      } else {
        // Gap longer than the sampling window breaks a sustained burst.
        panicStreak = 0;
      }

      lastX = event.clientX;
      lastY = event.clientY;
      lastTime = now;
    };

    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, [enabled, threshold]);
};
